/**
 * TasksByPersonView Component
 * Displays tasks grouped by assignee using UserTaskSummaryRow
 */

import { useMemo } from "react";
import { VStack } from "@chakra-ui/react";
import { BasicUser, User } from "features/User/models";
import { RelationType } from "models/v2/onboardingJourneyNetworkRelation";
import { Task } from "features/Resources/Task/models";
import { isTaskOpen } from "features/Resources/Task/helpers";
import UserTaskSummaryRow from "./UserTaskSummaryRow";
import UnassignedRoleRow from "./UnassignedRoleRow";

interface StakeholderWithTasks {
  user: User | BasicUser;
  roles: RelationType[];
  tasks: Task[];
}

interface UnassignedRole {
  role: RelationType;
  tasks: Task[];
}

interface RelationalPerson {
  user: User | BasicUser | undefined;
  roles: RelationType[];
}

interface TasksByPersonViewProps {
  tasks: Task[];
  currentUserId?: string;
  includeNotStarted?: boolean;
  hideClosed?: boolean;
  compact?: boolean;
  /** Relational people to always include (even if they have no tasks) */
  relationalPeople?: RelationalPerson[];
  /** Enables placeholder rows for unassigned stakeholder roles (with an assign action) */
  newHireJourneyId?: string;
  onRemind?: (user: User | BasicUser, tasks: Task[]) => void;
  resolveHref?: string;
  onTaskClick?: (task: Task) => void;
}

export default function TasksByPersonView({
  tasks,
  currentUserId,
  includeNotStarted = true,
  hideClosed = false,
  compact = false,
  relationalPeople = [],
  newHireJourneyId,
  onRemind,
  resolveHref,
  onTaskClick,
}: TasksByPersonViewProps) {
  // Group tasks by assignee. Relational people are always included, and unassigned
  // stakeholder roles are collected separately so their slot still surfaces.
  const { stakeholders, unassignedRoles } = useMemo(() => {
    const userMap = new Map<string, StakeholderWithTasks>();
    const unassignedMap = new Map<RelationType, UnassignedRole>();

    // First, add all relational people (they'll be included even without tasks)
    relationalPeople.forEach(({ user, roles }) => {
      if (!user) {
        roles.forEach((role) => {
          if (!unassignedMap.has(role)) unassignedMap.set(role, { role, tasks: [] });
        });
        return;
      }
      userMap.set(user.id, {
        user,
        roles,
        tasks: [],
      });
    });

    // Then, add tasks to their assignees (creating new entries for non-relational assignees).
    // A task whose role has no assignee attaches to that role's placeholder — created on
    // demand so a role's tasks always surface even when the role isn't a known stakeholder slot.
    tasks.forEach((task) => {
      if (!task.assignee) {
        const role = task.assigneeRole;
        if (!role) return;
        let placeholder = unassignedMap.get(role);
        if (!placeholder) {
          placeholder = { role, tasks: [] };
          unassignedMap.set(role, placeholder);
        }
        placeholder.tasks.push(task);
        return;
      }

      const userId = task.assignee.id;
      let entry = userMap.get(userId);
      if (!entry) {
        entry = {
          user: task.assignee,
          roles: task.assigneeRole ? [task.assigneeRole] : [],
          tasks: [],
        };
        userMap.set(userId, entry);
      }
      entry.tasks.push(task);
    });

    // Sort: current user first, then by number of open tasks (descending)
    const sortedStakeholders = Array.from(userMap.values()).sort((a, b) => {
      // Current user first
      if (a.user.id === currentUserId) return -1;
      if (b.user.id === currentUserId) return 1;

      // Then by open task count
      const aOpenCount = a.tasks.filter(isTaskOpen).length;
      const bOpenCount = b.tasks.filter(isTaskOpen).length;
      return bOpenCount - aOpenCount;
    });

    return { stakeholders: sortedStakeholders, unassignedRoles: Array.from(unassignedMap.values()) };
  }, [tasks, currentUserId, relationalPeople]);

  return (
    <VStack spacing={2} align="stretch" w="100%">
      {stakeholders.map((stakeholder) => (
        <UserTaskSummaryRow
          key={stakeholder.user.id}
          user={stakeholder.user}
          roles={stakeholder.roles}
          tasks={stakeholder.tasks}
          includeNotStarted={includeNotStarted}
          hideClosed={hideClosed}
          isCurrentUser={stakeholder.user.id === currentUserId}
          compact={compact}
          onRemind={onRemind ? () => onRemind(stakeholder.user, stakeholder.tasks) : undefined}
          resolveHref={stakeholder.user.id === currentUserId ? resolveHref : undefined}
          onTaskClick={onTaskClick}
        />
      ))}
      {newHireJourneyId &&
        unassignedRoles.map((entry) => (
          <UnassignedRoleRow
            key={`unassigned:${entry.role}`}
            role={entry.role}
            newHireJourneyId={newHireJourneyId}
            tasks={entry.tasks}
            includeNotStarted={includeNotStarted}
            hideClosed={hideClosed}
            onTaskClick={onTaskClick}
          />
        ))}
    </VStack>
  );
}
